import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Building2, FileText, Loader2, Globe, Briefcase, ArrowLeft, AlertTriangle, Info, CheckCircle, XCircle, TrendingUp, Clock, Target, Search } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
const CompanyInsights = () => {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [companyName, setCompanyName] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleAnalyze = async () => {
    if (!companyName.trim() && !companyUrl.trim() && !jobUrl.trim()) {
      toast.error("Please provide at least a company name, company URL, or job URL");
      return;
    }
    if (!user) {
      toast.error("Please sign in to use this feature");
      return;
    }
    setIsAnalyzing(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('trigger-company-insights', {
        body: {
          company_name: companyName.trim() || undefined,
          job_url: jobUrl.trim() || undefined
        }
      });
      if (error) throw error;
      toast.success("Company insights analysis started! Check back in a few minutes.");
      navigate('/company-insights-reports');
    } catch (error) {
      console.error('Error triggering company insights:', error);
      toast.error("Failed to start analysis. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };
  return <div className="min-h-screen bg-purple-50/50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/jobintel-360-reports')} className="h-9 w-9 p-0 bg-white">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center shadow-lg">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Advanced Company Insights</h1>
              <p className="text-muted-foreground mt-1">
                Deep intelligence to position yourself as the only logical choice
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/company-insights-reports')} className="gap-2 bg-white">
            <FileText className="h-4 w-4" />
            My Reports
          </Button>
        </div>

        {/* Hero Card */}
        <Card className="border-2 border-purple-400 bg-purple-50 shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Know Their Problems. Become Their Solution.
              </h2>
              <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed my-0 px-[19px]">
                Stop being one of <span className="text-destructive font-bold">hundreds of generic applicants</span>. 
                Our tool scours <span className="text-primary font-bold">4,000+ online data sources</span> to uncover the company's 
                biggest pain points, strategic goals, and what they truly value—so you can position yourself as 
                <span className="font-bold"> the clear and only answer</span> to their needs.
              </p>
            </div>

            {/* Two Info Links */}
            <div className="flex items-center justify-center gap-6 mt-4 mb-8">
              {/* Link 1: Why this matters (Amber themed) */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-amber-600 hover:text-amber-700 underline decoration-amber-400 underline-offset-4 font-semibold transition-colors">
                    <AlertTriangle className="w-4 h-4 mr-1.5" />
                    Why this matters
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="w-5 h-5" />
                      Why Company Intelligence Matters
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-6">
                      {/* Section 1: The Generic Application Problem */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <h3 className="font-semibold text-amber-800 mb-2">The Generic Application Problem</h3>
                        <p className="text-sm text-amber-900/80 mb-3">
                          Most job seekers send the same resume to every company. The problem is that 
                          hiring managers can spot a generic application instantly—and they 
                          <span className="font-bold text-destructive"> reject 75% of resumes</span> before 
                          a human ever sees them.
                        </p>
                        <div className="bg-white rounded-lg p-3 border border-amber-200">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-destructive">6-8 sec</div>
                            <div className="text-sm text-muted-foreground">Average time a recruiter spends on each resume</div>
                          </div>
                        </div>
                      </div>

                      {/* Section 2: Why Deep Research Wins */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">Why Deep Research Wins</h3>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Show you understand their challenges better than they do</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Align your experience with their strategic priorities</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Stand out from candidates who didn't do their homework</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 3: The Statistics */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">The Hard Truth: Application Statistics</h3>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left p-3 font-medium">Metric</th>
                                <th className="text-right p-3 font-medium">Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-t">
                                <td className="p-3">Recruiters who want tailored resumes</td>
                                <td className="p-3 text-right font-semibold">63%</td>
                              </tr>
                              <tr className="border-t bg-red-50">
                                <td className="p-3">Resumes rejected by ATS</td>
                                <td className="p-3 text-right font-semibold text-destructive">75%</td>
                              </tr>
                              <tr className="border-t">
                                <td className="p-3">Callback increase with tailoring</td>
                                <td className="p-3 text-right font-semibold text-success">+40-50%</td>
                              </tr>
                              <tr className="border-t bg-green-50">
                                <td className="p-3">Candidates who include relevant keywords</td>
                                <td className="p-3 text-right font-semibold">Only 51%</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 4: What Most Job Seekers Miss */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">What Most Job Seekers Miss</h3>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Company's current challenges and pain points</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Recent news, funding, or strategic shifts</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Leadership priorities and team culture</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Result: Generic applications that get ignored</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>

              <span className="text-muted-foreground">|</span>

              {/* Link 2: What you'll discover (Purple themed) */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-purple-600 hover:text-purple-700 underline decoration-purple-400 underline-offset-4 font-semibold transition-colors">
                    <Info className="w-4 h-4 mr-1.5" />
                    What you'll discover →
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-purple-600">
                      <Search className="w-5 h-5" />
                      JobIntel 360 Advanced Job Seeker Insights Report
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[75vh] pr-4">
                    <div className="space-y-6">
                      {/* Overview Section */}
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h3 className="font-semibold text-purple-800 mb-2">Three-Part Intelligence Package</h3>
                        <p className="text-sm text-purple-900/80 mb-3">
                          A comprehensive research package designed to help you prepare for interviews and make informed decisions about whether to pursue an opportunity. It delivers intelligence at three distinct levels—<span className="font-bold">company, department, and role</span>—so you understand not just what's in the job description, but the full context of where you'd be working.
                        </p>
                        <p className="text-sm text-purple-900/80">
                          Each report is built from verified, multi-source research (Glassdoor reviews, company financials, news, leadership profiles, salary databases, and interview experiences) with strict data integrity rules: <span className="font-bold">nothing fabricated, everything cited</span>.
                        </p>
                      </div>

                      <Separator />

                      {/* Report 1: Advanced Company Insights */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-purple-600" />
                          Report 1: Advanced Company Insights
                        </h3>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Company Overview</span> — What they do, when founded, headquarters, industry, and size</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Financial Health & Trajectory</span> — Revenue, growth rate, funding or market cap, profitability status</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Leadership Team</span> — CEO and key executives with tenure, background, and stability assessment</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Recent News & Developments</span> — Last 6 months of headlines: product launches, partnerships, expansions</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Culture Snapshot</span> — Glassdoor ratings (work-life balance, compensation, management, CEO approval)</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Employee Sentiment</span> — What employees love and complain about, with direct quotes</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Competitive Landscape</span> — Key competitors and market position assessment</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Red Flags</span> — Layoffs, restructuring, lawsuits, financial trouble—or confirmation none found</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Company Opportunity Score</span> — A 1-10 rating with balanced assessment</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Report 2: Advanced Department Insights */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-purple-600" />
                          Report 2: Advanced Department Insights
                        </h3>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Department Overview</span> — Which department, what it does, who it reports to, estimated team size</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Department Leadership</span> — Names, titles, verified LinkedIn profiles, tenure, and background</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Strategic Importance</span> — How this department drives company goals, key initiatives</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Department Priorities</span> — Current focus areas based on research and industry context</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Team Dynamics</span> — Working style, growth status, reporting structure</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Department-Specific Culture</span> — What employees say about this specific team</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Department Opportunity Assessment</span> — Strengths vs. potential challenges</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Report 3: Advanced Job Insights */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Target className="w-5 h-5 text-purple-600" />
                          Report 3: Advanced Job Insights
                        </h3>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Role Overview</span> — Title, department, location, employment type, remote status</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Why This Role Exists</span> — New headcount vs. backfill vs. expansion, with evidence</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Pain Points This Role Solves</span> — Real problems inferred from job requirements</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Compensation Analysis</span> — 25th, median, 75th percentile salaries with negotiation intel</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Interview Process</span> — Number of rounds, format, who you'll meet, timeline, difficulty</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Common Interview Questions</span> — 5+ real questions from Glassdoor and similar postings</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Career Path</span> — Where this role leads at 1-2 years, 3-5 years, and long-term</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Strategic Talking Points</span> — What to emphasize based on their pain points</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Stories to Prepare</span> — Specific scenarios to have ready based on role requirements</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Questions to Ask Them</span> — Smart questions that show research depth</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <p><span className="font-medium text-foreground">Overall Opportunity Score</span> — A 1-10 rating with clear recommendation</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* The Result */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h3 className="font-semibold text-green-800 mb-2">The Result</h3>
                        <p className="text-sm text-green-900/80">
                          A strategic preparation toolkit that arms you with talking points tailored to the company's actual pain points, interview questions that demonstrate genuine research, and a clear-eyed assessment of whether this opportunity is worth pursuing—<span className="font-bold">intelligence that transforms generic applicants into prepared, compelling candidates</span>.
                        </p>
                      </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>

            {/* 4 Feature Boxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 text-center border-2 border-purple-600 shadow-sm">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Search className="w-5 h-5 text-purple-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">4,000+ Sources</div>
                <div className="text-xs text-muted-foreground">Deep intelligence</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border-2 border-purple-600 shadow-sm">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">Pain Points</div>
                <div className="text-xs text-muted-foreground">Know their challenges</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border-2 border-purple-600 shadow-sm">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">+50% Callbacks</div>
                <div className="text-xs text-muted-foreground">With tailored approach</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border-2 border-purple-600 shadow-sm">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">~5 Minutes</div>
                <div className="text-xs text-muted-foreground">Fast results</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Section */}
        <Card className="border-2 border-purple-400 bg-white shadow-lg">
          <CardHeader className="bg-purple-50 border-b border-purple-200">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-purple-600" />
              Research a Company
            </CardTitle>
            <CardDescription>
              Enter company information to generate your personalized insights report
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="companyName" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Company Name
              </Label>
              <Input id="companyName" className="border-2 border-gray-400" placeholder="e.g., Google, Microsoft, Acme Corp..." value={companyName} onChange={e => setCompanyName(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyUrl" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Company Website (Optional)
              </Label>
              <Input id="companyUrl" type="url" className="border-2 border-gray-400" placeholder="https://company.com" value={companyUrl} onChange={e => setCompanyUrl(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobUrl" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Job Posting URL (Optional)
              </Label>
              <Input id="jobUrl" type="url" className="border-2 border-gray-400" placeholder="https://linkedin.com/jobs/..." value={jobUrl} onChange={e => setJobUrl(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                Including a job URL helps us provide role-specific company insights
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="context">Additional Context (Optional)</Label>
              <Textarea id="context" className="border-2 border-gray-400" placeholder="Any specific things you want to know about the company..." value={additionalContext} onChange={e => setAdditionalContext(e.target.value)} rows={3} />
            </div>

            <Button className="w-full bg-purple-600 hover:bg-purple-700" size="lg" onClick={handleAnalyze} disabled={isAnalyzing}>
              {isAnalyzing ? <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing Company...
                </> : <>
                  <Building2 className="h-4 w-4 mr-2" />
                  Generate Company Report
                </>}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>;
};
export default CompanyInsights;